import { Injectable } from '@angular/core';
import { FormBaseConfig, FieldConfig, FormInline, FormGrid, FormAccordion } from '../models/form-field.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { FormGroup, Validators, FormBuilder, AbstractControlOptions } from '@angular/forms';
import { FormGroupField } from '../components/form-group/form-group.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { FormComponent, FormDialogResult } from '../components/form/form.component';
import { FormDateRangeField } from '../components/form-date-range/form-date-range.model';
import { FormControlsContainerField } from '../components/form-controls-container/form-controls-container.modal';
import { FormEntityField } from '../components/form-entity/form-entity.model';
import { FormInputField } from '../components/form-input/form-input.model';
import { FormButtonField } from '../components/form-button/form-button.model';
import { FormGetDateOptions, FormGetInfoOptions, FormGetNumberOptions, FormGetPhoneOptions, FormGetTextOptions } from '../models/form-models';
import { FormLabelField } from '../components/form-label/form-label.model';
import { GioValidations } from 'src/app/core/validators/gio.validators';
import { FormListPickerField, ListPickerOption as ListPickerParams, ListPickerSection } from '../components/form-list-picker/form-list-picker.model';
import { FormSelectField } from '../components/form-select/form-select.model';
import { ColorPickerCardComponent } from '../../util/colors/color-picker-card/color-picker-card.component';
import { ColorPickerCardOptions } from '../../util/colors/color-picker-card/color-picker-card.model';
import { Entity, EntityType } from 'src/app/models/entities.model';
import { FormDateField } from '../components/form-date/form-date.model';
import * as moment from 'moment';
import { EntityService } from 'src/app/features/entity/services/entity.service';
import { TableField, TABLE_FIELD_OPTIONS } from 'src/app/features/entity/models/table-fields.model';
import { IdValueType } from 'src/app/core/helpers/indexables.helpers';
import { DocumentTypes } from 'src/app/features/documents/models/document.models';
import { FormFileUploadField } from '../components/form-file-upload/form-file-upload.model';

export interface ModalListPickerOptions {
  multi: boolean;
  title: string;
  label?: string;
  onFilter?: (term: string) => ListPickerParams[]
}

export type ModalSelectTextOptions = Pick<FormBaseConfig, 'actionButtons' | 'cancelButtonLabel' | 'submitButtonLabel'> & {
  title: string;
  label?: string;
}

export interface FormFile {
  file: File;
  data: string;
}
export interface FormConfigInstance<T = any> {
  state: BehaviorSubject<'idle' | 'edit'>;
  params: FormBaseConfig;
  fieldChanged: Subject<{ fieldName: string; fieldId: any; value: any }>;
  form: FormGroup;
  files?: { [index: string]: FormFile[] };
  data?: T;
  detectChanges$: Subject<any>;
  detectChanges: () => void;
  submit$: Subject<any>;
  cancel$: Subject<any>;
  isEnabledFn?: (fieldName: string, fieldId: any) => boolean;
  actions?: {
    idle: () => void;
    edit: () => void;
    submitted: (newFormData: T) => void;
  };
}

@Injectable({ providedIn: 'root' })
export class FormService {

  state: 'edit' | 'idle';

  constructor(
    private fb: FormBuilder,
    private modalDialog: MatDialog,
    private sEntity: EntityService
  ) { }


  createForm<T = any>(params: FormBaseConfig, data?: T): FormConfigInstance<T> {

    let fields: FieldConfig[] = [];
    if (params.template === 'accordion') {
      (params as FormAccordion).accordion.panels.forEach(pannel => {
        fields.push(...pannel.fields);
      });
    } else {
      fields = params.fields;
    }



    const a: FormConfigInstance<T> = {
      state: new BehaviorSubject('idle'),
      fieldChanged: new Subject(),
      detectChanges$: new Subject(),
      submit$: new Subject(),
      cancel$: new Subject(),
      files: {},
      params,
      form: this.createControls(fields),
      data,
      detectChanges: () => { }
    };

    a.detectChanges = () => {
      a.detectChanges$.next();
    };

    a.actions = {
      idle: () => {
        a.state.next('idle');
        a.form.setErrors(null);
        a.detectChanges$.next();
      },
      edit: () => {
        a.state.next('edit');
        a.detectChanges$.next();
      },
      submitted: (newFormData: T) => {
        a.data = newFormData;
        a.actions.idle();
      }

    };
    return a;
  }

  private createControls(fields: FieldConfig[], formOptions?: AbstractControlOptions | { [key: string]: any; } | null, group?: FormGroup) {
    const _group = group || this.fb.group({}, formOptions);
    fields.forEach(field => {

      if (['button', 'fileUpload', 'label'].includes(field.type)) { return; }

      if (field.type === 'container') {
        this.createControls((field as FormControlsContainerField).fields, formOptions, _group);
        return;
      }


      if (!field.name) { return; }
      if (field.type === 'group') {
        if (_group.controls[field.name]) {
          const group2 = _group.controls[field.name];
          this.createControls(
            (field as FormGroupField<any>).group,
            (field as FormGroupField<any>).groupValidation,
            (group2 as FormGroup)
          );
        } else {
          _group.addControl(field.name,
            this.createControls((field as FormGroupField<any>).group,
              (field as FormGroupField<any>).groupValidation)
          );
        }

      } else {
        this.addFieldControl(field, _group);
        if (field.type === 'daterange') {
          this.addFieldControl((field as FormDateRangeField<any>).field2, _group);
        }
      }

    });
    return _group;
  }

  private addFieldControl(field: FieldConfig, group: FormGroup) {
    const control = this.fb.control(
      null,
      this.bindValidations(field.validations || [])
    );
    group.addControl(field.name, control);

    if (field.type == 'entity') {
      const entityField = field as FormEntityField;
      if (entityField.entityFieldName) {
        const control2 = this.fb.control(null);
        group.addControl(entityField.entityFieldName, control2);
      }
    }
  }

  private bindValidations(validations: any) {
    if (validations.length > 0) {
      const validList = [];
      validations.forEach(valid => {
        validList.push(valid.validator);
      });
      return Validators.compose(validList);
    }
    return null;
  }

  createParams<T = any>(params: FormBaseConfig<T>): FormBaseConfig<T> {
    switch (params.template) {
      case 'accordion': return new FormAccordion(params as any);
      case 'grid': return new FormGrid(params);
      case 'inline': return new FormInline<T>(params);
      default: throw new Error('O template ' + params.template + ' não é conhecido');
    }
  }

  modal<T = any>(form: FormBaseConfig<T>, data?: Partial<T>, config?: MatDialogConfig): Promise<FormDialogResult<T>> {
    return new Promise((resolve) => {
      let _config: MatDialogConfig = {
        maxWidth: '95vw',
        maxHeight: '75vh',
        width: '600px',
        hasBackdrop: true,
        disableClose: true
      };

      if (config) {
        _config = Object.assign(_config, config);
      }

      _config.data = {
        service: this.createForm(form, data)
      };

      let dataResult = null;

      const ref = this.modalDialog.open(FormComponent, _config);
      if (form.onShowModal) {
        form.onShowModal(ref);
      }


      ref.beforeClosed().subscribe(result => {

        setTimeout(() => {
          if (!dataResult) {
            resolve(result as FormDialogResult<T>);
          }
        }, 300);
      });

      ref.afterClosed().subscribe(result => {
        dataResult = result;
        resolve(result as FormDialogResult<T>);
      });


    });
  }

  async getInfo<T = any>(options: FormGetInfoOptions): Promise<T> {
    try {
      const formParams: FormInline = {
        name: 'modal1',
        title: options.title,
        actionButtons: false,
        editable: true,
        initialState: 'edit',
        fields: [],
        buttons: [],
      };

      if (options.priorFields) {
        formParams.fields.push(...options.priorFields);
      }

      if (options.message) {
        formParams.fields.push(new FormLabelField({
          name: '_lbl1',
          label: options.message
        }));
      }

      const required = Object.hasKey(options, 'required') ? options.required : true;

      formParams.fields.push(new FormInputField({
        name: 'input',
        label: options.label,
        inputClasses: ['skip-keys'],
        inputType: options.inputType ?? 'text',
        mask: options.mask,
        required
      }));

      if (options.nextFields) {
        formParams.fields.push(...options.nextFields);
      }

      formParams.buttons.push(new FormButtonField({
        name: 'btnCancel',
        label: options.cancelText ?? 'Cancelar',
        closeModalOnClick: true,
        button: {
          type: 'action',
          color: 'warn',
          layout: 'basic',
          click: (service) => {
            service.cancel$.next();
          }
        }
      }));

      if (options.centerButtons) {
        formParams.buttons.push(...options.centerButtons);
      }

      formParams.buttons.push(new FormButtonField({
        name: 'btnConfirm',
        label: options.submitText ?? 'Consultar',
        color: 'primary',
        closeModalOnClick: true,
        button: {
          type: 'action',
          layout: 'basic',
          click: (service) => {
            service.submit$.next();
          }
        }
      }));


      const modal = await this.modal(new FormInline(formParams), { input: options.value });

      if (modal.event == 'submit') {
        return modal.data.input;
      }
      return null
    } catch (err) {

    }
  }

  getText(options: FormGetTextOptions): Promise<string> {
    return this.getInfo<string>(options as any);
  }

  getPhone(options: FormGetPhoneOptions) {
    const _options: FormGetInfoOptions = Object.assign(options, { inputType: 'tel' }) as any;
    return this.getInfo<string>(_options as any);
  }

  getNumber(options: FormGetNumberOptions): Promise<number> {
    const _options: FormGetInfoOptions = Object.assign(options, { inputType: 'number' }) as any;
    return this.getInfo<number>(_options);
  }


  async getColor(options?: ColorPickerCardOptions, active?: string): Promise<string> {
    const ref = this.modalDialog.open(ColorPickerCardComponent, {
      panelClass: 'no-padding',
      closeOnNavigation: true,
      disableClose: true,
      maxWidth: '95vw',
      maxHeight: '95vw',
      data: {
        color: active,
        options: options,
      }
    });

    const result = await ref.afterClosed().toPromise();
    return result;
  }

  async selectText(texts: string[], params: ModalSelectTextOptions): Promise<string> {
    const fields: any[] = [];
    if (params.label) {
      fields.push(new FormLabelField({
        name: 'lbl',
        label: params.label
      }));
    }

    fields.push(new FormSelectField({
      name: 'opts',
      options: texts.map((t, i) => ({ id: i, value: t }))
    }));

    const _p = this.createParams(new FormInline({
      editable: true,
      initialState: 'edit',
      title: params.title,
      fields,
      actionButtons: params.actionButtons ?? 'flat',
      submitButtonLabel: params.submitButtonLabel || 'SELECIONAR',
      cancelButtonLabel: params.cancelButtonLabel || 'CANCELAR'

    }));

    const results = await this.modal(_p);
    if (!results || results.event != 'submit') {
      return null;
    }

    if (results.data.opts == -1) {
      return null;
    }

    return texts[results.data.opts];
  }

  async selectItem<T = any>(items: T[], titleKey: keyof T, params: ModalSelectTextOptions): Promise<T> {
    const fields: any[] = [];
    if (params.label) {
      fields.push(new FormLabelField({
        name: 'lbl',
        label: params.label
      }));
    }

    fields.push(new FormSelectField({
      name: 'selected',
      options: items.map(item => {
        return {
          id: item,
          value: item[titleKey] as any
        }
      }),
      multi: false
    }));

    const form = this.createParams(new FormInline({
      editable: true,
      initialState: 'edit',
      title: params.title,
      actionButtons: params.actionButtons ?? 'flat',
      cancelButtonLabel: params.cancelButtonLabel ?? 'CANCELAR',
      submitButtonLabel: params.submitButtonLabel ?? 'SELECIONAR',
      fields
    }));

    const result = await this.modal<{ selected: T }>(form);
    if (!result || result.event != 'submit' || !result.data) {
      return null;
    }

    return result.data.selected
  }

  async selectItems<T = any>(items: T[], titleKey: keyof T, params: ModalSelectTextOptions): Promise<T[]> {
    const fields: any[] = [];
    if (params.label) {
      fields.push(new FormLabelField({
        name: 'lbl',
        label: params.label
      }));
    }

    fields.push(new FormSelectField({
      name: 'selecteds',
      options: items.map(item => {
        return {
          id: item,
          value: item[titleKey] as any
        }
      }),
      multi: true
    }));

    const form = this.createParams(new FormInline({
      editable: true,
      initialState: 'edit',
      title: params.title,
      actionButtons: params.actionButtons ?? 'flat',
      cancelButtonLabel: params.cancelButtonLabel ?? 'CANCELAR',
      submitButtonLabel: params.submitButtonLabel ?? 'SELECIONAR',
      fields
    }));

    const result = await this.modal<{ selecteds: T[] }>(form);
    if (!result || result.event != 'submit' || !result.data) {
      return null;
    }
    return result.data.selecteds
  }

  async confirmWithKeywork(keyword: string, message?: string, exactCase = false, title = 'Confirmar')
    : Promise<{ action: 'done' | 'cancel', passed: boolean }> {
    const fields = [
      new FormLabelField({
        label: `Para confirmar esta operação digite: <b>${keyword}</b>`,
        labelStyles: { marginBottom: '12px' }
      }),
      new FormInputField({
        label: '',
        name: 'informed',
        validations: [
          {
            name: 'required',
            message: 'Palavra passe obrigatória',
            validator: Validators.required
          },
          {
            name: 'isEqualString',
            message: `Para confirmar digite: ${keyword}`,
            validator: GioValidations.isEqualString(keyword, false)
          }
        ]
      })
    ];
    if (message) {
      fields.unshift(new FormLabelField({
        label: message,
        labelStyles: { marginBottom: '12px' }
      }));
    }
    const params = this.createParams(
      new FormInline({
        title,
        actionButtons: 'flat',
        initialState: 'edit',
        editable: true,
        fields
      })
    );
    const result = await this.modal(params);
    if (result.event == 'cancel') {
      return { action: 'cancel', passed: false };
    }
    if (result && result.event == 'submit' && result.data.informed) {
      if (exactCase) {
        return {
          action: 'done',
          passed: result.data.informed == keyword
        };
      }
      return {
        action: 'done',
        passed: result.data.informed.toLowerCase() == keyword.toLowerCase()
      };
    }
    return {
      action: 'cancel',
      passed: false
    };
  }

  async listPicker<T = any, R = T | T[] | any>(
    options: ListPickerParams<T>[],
    params: ModalListPickerOptions,
    initialData?: T | T[]
  ): Promise<R> {

    const fields: any[] = [];
    if (params.label) {
      fields.push(new FormLabelField({
        name: 'lbl',
        label: params.label
      }));
    }

    if (params.onFilter) {
      let filterTimer;
      let lastSearch;
      fields.push(new FormInputField({
        name: 'search_term',
        label: 'Pesquisar',
        suffix: { icon: 'search' },
        valueChanges: (field, value, service) => {
          if (filterTimer) {
            clearTimeout(filterTimer);
          }
          if (lastSearch == value) {
            return;
          }
          lastSearch = value;
          service.form.patchValue({ picker: null });

          filterTimer = setTimeout(() => {
            const values = params.onFilter(value);
            const picker: FormListPickerField = service.params.getFieldByName('picker') as any;
            picker.options = values;
            setTimeout(() => {
              service.detectChanges$.next(1);
            }, 10);
          }, 500)

          return false;
        }

      }));
    }

    fields.push(new FormListPickerField<T>({
      name: 'picker' as any,
      listType: params.multi ? 'multiSelection' : 'selection',
      options,
    }));


    const submitButton = new FormButtonField({
      label: 'SELECIONAR',
      button: { type: 'submit', layout: 'basic', color: 'primary' },
      disabled: (value: any, service) => {
        return !value.picker;
      }
    });
    const _form = this.createParams(new FormInline({
      title: params.title,
      editable: true,
      initialState: 'edit',
      actionButtons: 'raised',
      valueChanges: (value, fields) => {
        submitButton.disabled = !value.picker;
        return true;
      },
      buttons: [
        new FormButtonField({ label: 'CANCELAR', button: { type: 'cancel', layout: 'basic', color: 'warn' } }),
        submitButton
      ],
      fields
    }));

    const result = await this.modal(_form, initialData);
    if (result.event == 'submit') {
      return result.data.picker;
    }
    return null;

  }


  async entityPicker(entities: Entity[], params: ModalListPickerOptions) {
    let _initial: Entity[] = [].concat(...entities);

    params.onFilter = (term: string) => {
      if (!term) {
        return this.entitiesToPickerOptions(_initial);
      }

      let _entities = entities.filter(a => {
        if (a.name && a.name.toLowerCase().includes(term.toLowerCase())) {
          return true;
        }
        if (a.nickname && a.nickname.toLowerCase().includes(term.toLowerCase())) {
          return true;
        }

        if (a.document && a.document.toString().includes(term)) {
          return true;
        }

        return false;
      })

      return this.entitiesToPickerOptions(_entities);
    }

    return this.listPicker(this.entitiesToPickerOptions(_initial), params);
  }

  private entitiesToPickerOptions(entities: Entity[]) {
    if (!entities || entities.length == 0) {
      return [];
    }
    return entities.map(e => {
      let lines: string[] = [];
      if (e.nickname) {
        lines.push(e.nickname);
      }

      if (e.source_name) {
        lines.push(Entity.getEntityTypeName(e.source_name));
      }

      let p: ListPickerParams = {
        title: e.name.capitalize(),
        value: e,
        lines
      }

      return p;
    });
  }

  async listPickerWithsections<T = any>(
    sections: ListPickerSection<T>[],
    params: ModalListPickerOptions
  ): Promise<T> {
    const fields: any[] = [];
    if (params.label) {
      fields.push(new FormLabelField({
        name: 'lbl',
        label: params.label
      }));
    }

    fields.push(new FormListPickerField<T>({
      name: 'picker' as any,
      listType: params.multi ? 'multiSelection' : 'selection',
      sections
    }));


    const _form = this.createParams(new FormInline({
      title: params.title,
      editable: true,
      initialState: 'edit',
      actionButtons: 'raised',
      buttons: [
        new FormButtonField({ label: 'CANCELAR', button: { type: 'cancel', layout: 'basic', color: 'warn' } }),
        new FormButtonField({ label: 'SELECIONAR', button: { type: 'submit', layout: 'basic', color: 'primary' } })
      ],
      fields
    }));

    const result = await this.modal(_form);
    if (result.event == 'submit') {
      return result.data.picker;
    }
    return null;

  }

  async getDate(options: FormGetDateOptions): Promise<moment.Moment> {

    let fields: FieldConfig[] = [];

    if (options.label) {
      fields.push(new FormLabelField({
        name: 'lbl',
        label: options.label
      }));
    }

    fields.push(new FormDateField({
      name: 'date',
      label: 'Data',
      required: true,
      date: {
        min: options?.min,
        max: options?.max
      }
    }));


    const params = this.createParams(new FormInline({
      title: options.title || 'Selecione a data',
      editable: true,
      initialState: 'edit',
      buttons: [],
      actionButtons: 'flat',
      fields,

    }));

    params.buttons.push(new FormButtonField({
      name: 'btnCancel',
      label: options.cancelText ?? 'Cancelar',
      closeModalOnClick: true,
      button: {
        type: 'action',
        color: 'warn',
        layout: 'basic',
        click: (service) => {
          service.cancel$.next();
        }
      }
    }));


    params.buttons.push(new FormButtonField({
      name: 'btnConfirm',
      label: options.submitText ?? 'Selecionar',
      color: 'primary',
      closeModalOnClick: true,
      button: {
        type: 'action',
        layout: 'basic',
        click: (service) => {
          service.submit$.next();
        }
      }
    }));

    const result = await this.modal(params, { date: options.initalValue || moment().toString() });

    if (!result || result.event != 'submit' || !result.data.date) {
      return null;
    }

    return result.data.date;
  }

  async selectEntityfromContexts(options?: { types?: EntityType[], entities?: Entity[], title: string, description?: string, multi?: boolean }) {
    const groups: { sourceName: string, title: string, entities: Entity[] }[] = [];

    let targetEntities = options?.entities || await this.sEntity.getContextEntities();

    if (options && options.types && options.types.length > 0) {
      targetEntities = targetEntities.filter(entity => {
        return options.types.includes(entity.source_name as any);
      });
    }

    targetEntities.forEach(entity => {
      let group = groups.find(g => g.sourceName == entity.source_name);
      if (!group) {
        group = { sourceName: entity.source_name, title: Entity.getEntityTypeName(entity.source_name, true), entities: [] };
        groups.push(group);
      }

      group.entities.push(entity);
    })


    const selected = await this.listPickerWithsections(
      groups.map(group => {

        return {
          title: group.title,
          options: group.entities.map(entity => {
            return {
              title: entity.name,
              value: entity,
              lines: [
                entity.nickname,
                entity.email,
                (entity.is_company ? 'CNPJ: ' : 'CPF:') + Entity.formatDocument(entity, true)
              ]
            }
          })
        }
      }), {
      multi: options.multi,
      title: options.title,
      label: options.description,
    });

    return selected;
  }

  async askForTableField<T = any>(options: { title: string, fields: TableField[], requireds?: string[] }, initialData: T): Promise<T> {
    let fields: FieldConfig[] = [];


    if (!options.fields || options.fields.length == 0) {
      return null;
    }

    const requireds = options.requireds || [];

    options.fields.forEach(option => {

      if (option.type == 'string') {
        fields.push(new FormInputField({
          name: option.name,
          label: option.label,
          required: requireds.length > 0 && requireds.includes(option.name as any),
        }));
      }

      if (option.type == 'email') {
        fields.push(new FormInputField({
          name: option.name,
          label: option.label,
          validations: [{ name: 'email', message: 'E-mail inválido', validator: Validators.email }],
          required: requireds.length > 0 && requireds.includes(option.name as any),
        }));
      }

      if (option.type == 'phone') {
        fields.push(new FormInputField({
          name: option.name,
          label: option.label,
          mask: {
            mask: '(00) 0000-00009',
            showMaskTyped: true,
          },
          required: requireds.length > 0 && requireds.includes(option.name as any),
        }));
      }

      if (option.type.startsWith('date')) {
        const date: { min?: Date, max?: Date } = {}
        const _params = option.type.split(':');
        if (_params.length > 1) {
          const params2 = _params[1].split(';');
          if (params2.length > 1) {
            for (const _p of params2) {
              const p = _p.split('=');
              if (p.length > 1) {
                if (p[0] == 'min') {
                  date.min = moment(p[1]).toDate();
                }
                if (p[0] == 'max') {
                  date.max = moment(p[1]).toDate();
                }
              }
            }
          }
        }


        fields.push(new FormDateField({
          name: option.name,
          label: option.label,
          date,
          required: requireds.length > 0 && requireds.includes(option.name as any),
        }));
      }

      if (option.type.startsWith('integer')) {
        const _optionParams: { min?: number, max?: number, step?: number } = {}
        const _p1 = option.type.split(':');
        if (_p1.length > 1) {

          let _pp = _p1[1].asObjectParams<{ min: number, max: number, step: number, combo: 'true' | 'false' }>(';');

          if (_pp.combo == 'true' && _pp.min && _pp.max) {
            const _options: IdValueType[] = [];
            let { min, max, step = 1 } = _pp;

            let value = +min;
            while (value <= +max) {
              _options.push({
                id: value,
                value: value.toString()
              })
              value += +step;
            }

            fields.push(new FormSelectField({
              name: option.name,
              label: option.label,
              options: _options,
              required: requireds.length > 0 && requireds.includes(option.name as any),
            }));
          } else {
            fields.push(new FormInputField({
              name: option.name,
              label: option.label,
              inputType: 'number',
              options: {
                min: _pp.min,
                max: _pp.max,
                step: _pp.step
              },
              required: requireds.length > 0 && requireds.includes(option.name as any),
            }));
          }


          const _p2 = _p1[1].split(';');

          if (_p2.length > 1) {
            for (const _p3 of _p2) {
              const _p4 = _p3.split('=');
              if (_p4[0] == 'min') {
                _optionParams.min = parseInt(_p4[1]);
              } else if (_p4[0] == 'max') {
                _optionParams.max = parseInt(_p4[1]);
              } else if (_p4[0] == 'step') {
                _optionParams.step = parseInt(_p4[1]);
              }
            }
          }
        }

        if (_optionParams.min && _optionParams.max) {


        } else {


        }

      }

      if (option.type == 'options') {

        let fieldName = option.name;
        let _options = [];
        let fieldOptions: (string | IdValueType<any, string>)[] = [];

        if (!option.options) {
          option.options = `key=${fieldName}`;
        }

        const optionParams = String.asObjectParams(option.options, '=');
        if (!optionParams.key) {
          optionParams.key = fieldName;
        }

        fieldOptions = TABLE_FIELD_OPTIONS[optionParams.key];

        //analise options. and convert to idValue if necessary
        for (const option of fieldOptions) {
          if (typeof option == 'string') {
            _options.push({ id: option, value: option });
          } else {
            _options.push(option);
          }
        }

        fields.push(new FormSelectField({
          name: option.name,
          label: option.label,
          options: _options,
          required: requireds.length > 0 && requireds.includes(option.name as any),
        }));

      }


    });

    const form = new FormInline({
      initialState: 'edit',
      editable: true,
      title: options.title ?? 'Preencha as informações abaixo',
      actionButtons: 'flat',
      fields
    });

    const result = await this.modal(form, initialData);
    if (result && result.event == 'submit') {
      return result.data;
    }
    return null;
  }

  async askForDocumentFiles(options: {
    title: string,
    maxFiles?: number,
    fields: { required: DocumentTypes, values: Document[] }[],
  }) {

    let fields = [];

    for (const field of options.fields) {
      const { required, values } = field;

      fields.push(new FormFileUploadField({
        name: 'fid_' + options.fields.indexOf(field),
        label: required.name,
        maxFiles: options.maxFiles,
        template: {
          type: 'text'
        }
      }))

    }

    const form = new FormInline({
      initialState: 'edit',
      editable: true,
      title: options.title ?? 'Preencha as informações abaixo',
      actionButtons: 'flat',
      fields
    });

    const result = await this.modal(form);

    if (!result || result.event != 'submit') {
      return null;
    }

    let response: { field: DocumentTypes, files: { file: File, data: string }[] }[] = [];
    const fids = Object.keys(result.data);
    for (const fid of fids) {
      const resultData = result.data[fid];
      const fieldIdx = +(fid.split('_')[1]);
      const requestedField = options.fields[fieldIdx];
      response.push({
        field: requestedField.required,
        files: resultData
      })
    }



    return response;
  }

}
